import { z } from "zod";

const EnvSchema = z.object({
  API_BASE_URL: z.string().url(),
  API_OPENAPI_URL: z.string().url(),
  ENV: z.enum(["local", "sandbox", "homolog", "prod"]).default("local"),
  DEV_BYPASS_USER_EMAIL: z.string().email().optional(),
  GOOGLE_OAUTH_CLIENT_ID: z.string().optional(),
});

export type Env = z.infer<typeof EnvSchema>;

/**
 * Trata `""` como ausente. O Dockerfile do front declara `ARG VITE_*=`
 * (defaults vazios) e exporta tudo via `ENV VITE_*=$VITE_*`, entao mesmo
 * build-args nao informados viram strings vazias no bundle. Zod com
 * `.email().optional()`/`.url().optional()` aceita `undefined` mas rejeita
 * `""` -- isso quebraria o app em qualquer ambiente sem todas as VITE_*
 * setadas. Filtrar aqui mantem o schema legivel e o erro previsivel.
 */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

function parseEnv(): Env {
  const apiBase = nonEmpty(import.meta.env.VITE_API_BASE_URL);
  const raw = {
    API_BASE_URL: apiBase,
    API_OPENAPI_URL:
      nonEmpty(import.meta.env.VITE_API_OPENAPI_URL) ||
      (apiBase ? `${apiBase}/openapi.json` : undefined),
    ENV: nonEmpty(import.meta.env.VITE_ENV),
    DEV_BYPASS_USER_EMAIL: nonEmpty(
      import.meta.env.VITE_DEV_BYPASS_USER_EMAIL,
    ),
    GOOGLE_OAUTH_CLIENT_ID: nonEmpty(
      import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
    ),
  };
  const result = EnvSchema.safeParse(raw);
  if (!result.success) {
    console.error("Variaveis VITE_* invalidas:", result.error.format());
    throw new Error("Variaveis VITE_* invalidas (veja console)");
  }
  return result.data;
}

export const env = parseEnv();
